import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useInitiateConfigQuery } from '../../../../../queries/useInitiateConfigQuery';
import { IBestDataset } from '../../../../../types/analytics';
import { IMarker } from '../../../../../types/marker';
import { AnalyticsSection } from '../../../Analytics/components/Layout/AnalyticsSection';
import {
  REQUEST_REPORT_STATUS,
  useOndemandParams,
} from '../../hooks/useOndemandParams';
import styles from './Metocean.module.scss';
import { ISampleReport } from './ReportCard';
import ReportsInputForm from './ReportsInputForm';
import { ReportsList } from './ReportsList';
import { SAMPLE_REPORTS } from './sample-reports';

const { Text } = Typography;

interface ReportsProps {
  datasets: IBestDataset[];
  point?: IMarker;
  defaultDataset?: { key: string; value: string }[];
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void;
}

export default function ReportsOnDemand(props: ReportsProps) {
  const { datasets, defaultDataset, point } = props;

  const { config } = useInitiateConfigQuery();
  const {
    datasetSW,
    datasetHD,
    datasetWind,
    timeStep,
    startDate,
    endDate,
    requestReportStatus,
    isHDDatasetOffline,
    isSWDatasetOffline,
    isWindDatasetOffline,
    isSomeSelectedDatasetOffline,
    isPayloadForReportCallValid,
    requestedReportsCount,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    handleDatasetChange,
    handleRequestReport,
  } = useOndemandParams({
    datasets,
    defaultDataset,
    pointName: point?.label!,
    location: point?.coordinates!,
    setDatasetIsOfflineWarningVisible: props.setDatasetIsOfflineWarningVisible,
  });

  const isReportError = requestReportStatus === REQUEST_REPORT_STATUS.ERROR;
  const isReportSuccess = requestReportStatus === REQUEST_REPORT_STATUS.SUCCESS;
  const isReportPending = requestReportStatus === REQUEST_REPORT_STATUS.PENDING;

  const isRequestReportDisabled =
    config?.data?.isRequestReportButtonEnabled === 'false' ||
    !config?.data?.isRequestReportButtonEnabled ||
    isSomeSelectedDatasetOffline;

  const isReRequestReportDisabled =
    config?.data?.isMultipleReportRequestsEnabled === 'false' ||
    !config?.data?.isRequestReportButtonEnabled;

  const handleExcelDownload = (report: ISampleReport): void => {
    window.open(report.zipUrl, '_blank');
  };

  const handlePdfDownload = (report: ISampleReport): void => {
    window.open(report.pdfUrl, '_blank');
  };

  const plotInputContent = useMemo(
    () => (
      <div className={styles.plotInputContainer}>
        <ReportsInputForm
          datasets={datasets}
          datasetSW={datasetSW}
          datasetHD={datasetHD}
          datasetWind={datasetWind}
          timeStep={timeStep}
          onSelectDataset={handleDatasetChange}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={doStartDateChange}
          onEndDateChange={doEndDateChange}
          disabledStartDate={disabledStartDate}
          disabledEndDate={disabledEndDate}
          isOfflineDataset={{
            sw: isSWDatasetOffline,
            hd: isHDDatasetOffline,
            wind: isWindDatasetOffline,
          }}
        />
      </div>
    ),
    [
      datasets,
      datasetSW,
      datasetHD,
      datasetWind,
      timeStep,
      handleDatasetChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      isSWDatasetOffline,
      isHDDatasetOffline,
      isWindDatasetOffline,
    ]
  );

  const successInfo = useMemo(
    () => (
      <div className={styles.reportSuccess}>
        <CheckCircleOutlined style={{ fontSize: '24px' }} />
        <span>Your request has been successfully submitted!</span>
      </div>
    ),
    []
  );

  const errorInfo = useMemo(
    () => (
      <div className={styles.reportError}>
        Something went wrong when processing your request
      </div>
    ),
    []
  );

  const offlineDatasetInfo = useMemo(
    () => (
      <div className={styles.reportError}>
        Some of the selected datasets are not available online.{' '}
        <div>
          {'Please '}
          <Link to={'/contact'}> {'contact us'}</Link>
          {' for more information or data requests.'}
          <div></div>
        </div>
      </div>
    ),
    []
  );

  const defineIsRequestReportDisabled = () => {
    if (isRequestReportDisabled) return true;

    if (isPayloadForReportCallValid) {
      const wasReportAlreadyRequested = requestedReportsCount > 0;
      if (wasReportAlreadyRequested && isReRequestReportDisabled) return true;
    }
    return false;
  };

  const isRequestReportConditionallyDisabled = defineIsRequestReportDisabled();

  const updateContent = useMemo(
    () => (
      <div className={styles.requestReportContainer}>
        <>
          <Button
            type='primary'
            id='Reports-Update_Button'
            onClick={handleRequestReport}
            style={{
              width: '130px',
              height: '36px',
              marginBottom: '8px',
            }}
            loading={isReportPending}
            disabled={isRequestReportConditionallyDisabled}
          >
            {!isReportPending && <span>Request Report</span>}
          </Button>
          {isReportError && errorInfo}
          {isSomeSelectedDatasetOffline && offlineDatasetInfo}
        </>
        {isReportSuccess && successInfo}
      </div>
    ),
    [
      isReportSuccess,
      handleRequestReport,
      isReportPending,
      isReportError,
      errorInfo,
      isSomeSelectedDatasetOffline,
      isRequestReportConditionallyDisabled,
      offlineDatasetInfo,
      successInfo,
    ]
  );

  const reportsContent = useMemo(() => {
    const reports = SAMPLE_REPORTS;
    return (
      <ReportsList
        reports={reports}
        onPdfDownload={handlePdfDownload}
        onExcelDownload={handleExcelDownload}
      />
    );
  }, []);

  const additionalContent = useMemo(
    () => (
      <div className={styles.additionalInfoContainer}>
        <Text type='secondary'>
          <Text strong>Delivery</Text>:{' '}
          <i>
            The requested report (PDF and Excel) will be provided by email
            within 2 hours
          </i>
        </Text>

        <Text type='secondary' className={styles.disclaimerText}>
          <Text strong style={{ color: 'inherit' }}>
            Disclaimer
          </Text>
          :{' '}
          <i>
            The Reports are released in beta version. The content and access may
            change.
          </i>
        </Text>
      </div>
    ),
    []
  );

  return (
    <>
      <AnalyticsSection
        plotInputContent={plotInputContent}
        updateContent={updateContent}
        chartContent={reportsContent}
        isLoading={false}
        additionalContent={additionalContent}
      />

      {requestReportStatus}
    </>
  );
}
